/**
 * Where a fixed-resolution 2D surface actually lands inside its parent — the
 * whole geometry of `ctx.graphics.create2D`, in one pure file.
 *
 * A pixel-art game renders at a fixed internal resolution (Dome Warden: 480×270)
 * and is scaled up for display. Three things then have to agree, or the app is
 * subtly wrong in a way that reads as a game bug rather than a layout bug:
 *
 *   1. the CSS size the canvas is stretched to,
 *   2. where it sits in a parent of a different aspect ratio (the letterbox),
 *   3. how a pointer's client coords map back to internal coords.
 *
 * Getting (3) out of step with (1) means every click lands somewhere the player
 * did not aim, worst at the edges and invisible in the middle — so it survives
 * casual testing. Keeping the maths here, pure and tested, is the point.
 *
 * The backing store is deliberately NOT multiplied by devicePixelRatio. For
 * pixel art the whole intent is that one game pixel becomes an N×N block; a DPR
 * multiply resamples it and the art goes soft. That decision lives in the host,
 * but it is why these functions only ever talk about CSS pixels.
 */

/** @typedef {"pixel" | "fit" | "stretch"} Surface2DScaling */

/**
 * Scale factor for a fixed `w`×`h` surface inside a `pw`×`ph` box.
 *
 * `"pixel"` snaps to a whole number ONLY at 2× and above. Snapping below that
 * would send 1.9× down to 1×, throwing away nearly half the window to protect
 * a crispness nobody can see at that size — so under 2× it scales smoothly and
 * accepts the resampling.
 */
export function resolveScale(pw, ph, w, h, scaling = "fit") {
  if (!(w > 0) || !(h > 0)) return 1;
  const raw = Math.min(pw / w, ph / h);
  if (!(raw > 0) || !Number.isFinite(raw)) return 0;
  if (scaling === "pixel" && raw >= 2) return Math.floor(raw);
  return raw;
}

/**
 * Full placement: CSS size of the canvas and its offset inside the parent.
 * `"stretch"` fills the parent and abandons the aspect ratio; the other modes
 * centre a correctly-proportioned canvas and leave letterbox bars.
 */
export function computeLayout(pw, ph, w, h, scaling = "fit") {
  if (scaling === "stretch") {
    return { scale: 0, cssW: pw, cssH: ph, offsetX: 0, offsetY: 0 };
  }
  const scale = resolveScale(pw, ph, w, h, scaling);
  const cssW = w * scale;
  const cssH = h * scale;
  return {
    scale,
    cssW,
    cssH,
    // Math.round keeps the canvas on whole pixels; a half-pixel offset makes
    // even integer-scaled art blurry, which is the exact failure "pixel" mode
    // exists to prevent.
    offsetX: Math.round((pw - cssW) / 2),
    offsetY: Math.round((ph - cssH) / 2),
  };
}

/**
 * Client coords → internal surface coords.
 *
 * Driven by the canvas's own bounding rect rather than a recomputed layout, so
 * it stays correct through anything CSS does to the element on the way down —
 * a scaled ancestor, a transform, the OS window's own zoom. Values are NOT
 * clamped: a caller that needs "did this land on the surface" should ask
 * `isInside`, because a drag that leaves the canvas still wants real coords.
 */
export function toLocal(clientX, clientY, rect, w, h) {
  const rw = rect.width || 1;
  const rh = rect.height || 1;
  return {
    x: ((clientX - rect.left) / rw) * w,
    y: ((clientY - rect.top) / rh) * h,
  };
}

/** True when an internal coord pair falls within the surface. */
export function isInside(p, w, h) {
  return p.x >= 0 && p.y >= 0 && p.x < w && p.y < h;
}
